import React, { useEffect, useState } from "react";
import _ from "lodash"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  MenuItem,
  InputAdornment,
  IconButton as MuiIconButton,
  Grid2,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { IconX, IconEye, IconEyeOff } from "@tabler/icons-react";
import { useForm, Controller } from "react-hook-form";
import dataConstant from "../../constants/dataConstants";
import formatPhoneNumber from "../../Helper/phoneNumberFormatter";
import { encrypt } from "../../Helper/encryptHelper";
const roles = ["Admin", "Staff", "Marketer"];

const UsersModal = ({ open, onClose, onSave, selectedRow, cityData }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
   
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      code: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      password: "",
      confirmPassword: "",
      notify: false,
      cityId: "",
      isMarketer: false,
      marketerCode: ""
    },
  });

  const phone = watch("phone");
  const roleName = watch("role")
  const isMarketer = watch("isMarketer");

  useEffect(() => {
    setValue("phone", formatPhoneNumber(phone || ""));
  }, [phone]);

  useEffect(() => {
    if(!selectedRow){
      if(roleName === "Marketer"){
         setValue("isMarketer", true);
      }
    }
  }, [roleName]);

  useEffect(() => {
    if (selectedRow) {
      const {
        id,
        fullName = "",
        email = "",
        phoneNumber = "",
        roleName = "",
        status = "",
        receiveEmailOnClientSubmission,
        marketerCode,
        isMarketer
      } = selectedRow;

      const [firstName = "", lastName = ""] = fullName.trim().split(" ");
      let selectedCity = ""
      if(selectedRow.cityUnqGUID !== null){
        const city = _.find(cityData, (x) => x.unqGuid === selectedRow.cityUnqGUID);
        if(city){
          selectedCity = city
        }
      }

      reset({
        id,
        firstName,
        lastName,
        email,
        phone: phoneNumber,
        role: roleName,
        status,
        password: "",
        confirmPassword: "",
        notify: receiveEmailOnClientSubmission,
        marketerCode,
        isMarketer,
        cityId: selectedCity
      });
    } else {
      reset({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        status: "",
        password: "",
        confirmPassword: "",
        notify: false,
      });
    }
  }, [open, selectedRow, reset]);

  const onSubmit = (data) => {
    const { id, email, firstName, lastName, phone, role, password, status, notify } =
      data;
    
    const payload = {
      id,
      email,
      firstName,
      lastName,
      phoneNumber: phone,
      roleName: role,
      receiveEmailOnClientSubmission: notify
    };

    if(data.isMarketer){
      payload.isMarketer = true;
      payload.marketerCode = data.marketerCode;

      const city = _.find(cityData, (x) => x.unqGuid === data?.cityId?.unqGuid);
      if(city){
        payload.cityUnqGUID = city.unqGuid
      }
    }
    // Conditionally add password and status only if present
    if (password?.trim()) {
      payload.password = encrypt(password.trim());
    }

    if (status?.trim()) {
      payload.status = status.trim();
    }
    onSave(payload);
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableAutoFocus
    >
      <DialogTitle>
        {selectedRow ? "Edit User" : "Add User"}
        <IconButton onClick={onClose} sx={{ padding: 0 }}>
          <IconX />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: "First name is required",
                  maxLength: {
                    value: 20,
                    message: "First name must be at most 20 characters",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    label="First Name"
                    required
                    fullWidth
                    {...field}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: "Last name is required",
                  maxLength: {
                    value: 20,
                    message: "Last name must be at most 20 characters",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    label="Last Name"
                    required
                    fullWidth
                    {...field}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12 }}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    label="Email"
                    fullWidth
                    required
                    {...field}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    inputProps={{ autoComplete: "off" }}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone is required",
                  validate: (val) =>
                    val.replace(/\D/g, "").length === 10 ||
                    "Invalid phone number",
                }}
                render={({ field }) => (
                  <TextField
                    label="Phone"
                    fullWidth
                    required
                    {...field}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Controller
                name="role"
                control={control}
                rules={{ required: "Admin dashboard role is required" }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Admin Dashboard Role"
                    fullWidth
                    required
                    {...field}
                    error={!!errors.role}
                    helperText={errors.role?.message}
                  >
                    <MenuItem value="">Select Admin Dashboard Role</MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid2>

            {!selectedRow && (
              <>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: "Password is required",
                      pattern: {
                        value: dataConstant.passwordRegex,
                        message:
                          "Password must be 6-16 characters and include uppercase, lowercase, number, and special character",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        label="Password"
                        fullWidth
                        required
                        type={showPassword ? "text" : "password"}
                        {...field}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <MuiIconButton
                                onClick={() => setShowPassword((v) => !v)}
                              >
                                {showPassword ? <IconEyeOff /> : <IconEye />}
                              </MuiIconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: "Confirm password is required",
                      validate: (val) =>
                        val === watch("password") || "Passwords do not match",
                    }}
                    render={({ field }) => (
                      <TextField
                        label="Confirm Password"
                        fullWidth
                        required
                        type={showConfirm ? "text" : "password"}
                        {...field}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <MuiIconButton
                                onClick={() => setShowConfirm((v) => !v)}
                              >
                                {showConfirm ? <IconEyeOff /> : <IconEye />}
                              </MuiIconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid2>
              </>
            )}

            <Grid2 size={{ xs: 12, sm: 12 }}>
              <Controller
                name="notify"
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="Receive email notifications for new client submissions?"
                    />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12 }}>
              <Controller
                name="isMarketer"
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        control={<Checkbox {...field} checked={!!field.value} />}
                        label="Do you want to make user as marketer?"
                    />
                )}
              />
            </Grid2>
                
            {isMarketer &&
              <>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="marketerCode"
                    control={control}
                    rules={{
                      required: "Marketer code is required",
                      maxLength: {
                        value: 20,
                        message: "Marketer code must be at most 20 characters",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        label="Marketer Code"
                        required
                        fullWidth
                        disabled={selectedRow && selectedRow.marketerCode !== null}
                        {...field}
                        value={field.value || ""}
                        onChange={(e)=>{
                          const value = e.target.value?.replace(/[^a-z0-9]/gi, "");
                          const formattedValue = value.toUpperCase();
                          field.onChange(formattedValue);
                        }}
                        error={!!errors.marketerCode}
                        helperText={errors.marketerCode?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="cityId"
                    control={control}
                    rules={{ required: "City is required" }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        fullWidth
                        options={cityData}
                        getOptionLabel={(option) =>
                          option ? `${option.cityName}` : ""
                        }
                        onChange={(_, newValue) => field.onChange(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label="Select City"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid2>
              </>
            }
          </Grid2>
        </form>
      </DialogContent>

      <DialogActions>
        <Button size="small" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsersModal;
